import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronUp,
  ChevronDown,
  TrendingDown,
  TrendingUp,
  Minus,
  AlertTriangle,
  User,
  Filter,
} from 'lucide-react';
import { Employee } from '../data/employees';

interface EmployeeTableProps {
  employees: Employee[];
  searchQuery: string;
}

type SortKey = 'name' | 'department' | 'riskScore' | 'engagementTrend';
type SortDirection = 'asc' | 'desc';

const EmployeeTable: React.FC<EmployeeTableProps> = ({ employees, searchQuery }) => {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>('riskScore');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filterRisk, setFilterRisk] = useState<string>('all');
  const [filterDepartment, setFilterDepartment] = useState<string>('all');

  const departments = useMemo(() => {
    return Array.from(new Set(employees.map((e) => e.department))).sort();
  }, [employees]);

  const filteredAndSorted = useMemo(() => {
    let result = [...employees];

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.name.toLowerCase().includes(query) ||
          e.email.toLowerCase().includes(query) ||
          e.role.toLowerCase().includes(query) ||
          e.department.toLowerCase().includes(query)
      );
    }

    // Apply risk filter
    if (filterRisk !== 'all') {
      result = result.filter((e) => e.riskLevel === filterRisk);
    }

    // Apply department filter
    if (filterDepartment !== 'all') {
      result = result.filter((e) => e.department === filterDepartment);
    }

    // Sort
    result.sort((a, b) => {
      let aVal: any = a[sortKey];
      let bVal: any = b[sortKey];

      if (sortKey === 'engagementTrend') {
        const trendOrder = { declining: 2, stable: 1, improving: 0 };
        aVal = trendOrder[a.engagementTrend];
        bVal = trendOrder[b.engagementTrend];
      }

      if (typeof aVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return result;
  }, [employees, searchQuery, sortKey, sortDirection, filterRisk, filterDepartment]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const getRiskBadgeStyle = (level: string) => {
    switch (level) {
      case 'High':
        return { background: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' };
      case 'Medium':
        return { background: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B' };
      case 'Low':
        return { background: 'rgba(16, 185, 129, 0.1)', color: '#10B981' };
      default:
        return {};
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'declining':
        return <TrendingDown size={16} color="#EF4444" />;
      case 'improving':
        return <TrendingUp size={16} color="#10B981" />;
      default:
        return <Minus size={16} color="#9CA3AF" />;
    }
  };

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.filterGroup}>
          <Filter size={16} color="#6B7280" />
          <span style={styles.filterLabel}>Filters:</span>
        </div>

        <select
          style={styles.select}
          value={filterRisk}
          onChange={(e) => setFilterRisk(e.target.value)}
        >
          <option value="all">All Risk Levels</option>
          <option value="High">High Risk</option>
          <option value="Medium">Medium Risk</option>
          <option value="Low">Low Risk</option>
        </select>

        <select
          style={styles.select}
          value={filterDepartment}
          onChange={(e) => setFilterDepartment(e.target.value)}
        >
          <option value="all">All Departments</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>

        <span style={styles.resultCount}>
          {filteredAndSorted.length} employees
        </span>
      </div>

      {/* Table */}
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>
                <button
                  style={styles.sortButton}
                  onClick={() => handleSort('name')}
                >
                  Employee
                  <SortIndicator active={sortKey === 'name'} direction={sortDirection} />
                </button>
              </th>
              <th style={styles.th}>
                <button
                  style={styles.sortButton}
                  onClick={() => handleSort('department')}
                >
                  Department
                  <SortIndicator active={sortKey === 'department'} direction={sortDirection} />
                </button>
              </th>
              <th style={styles.th}>
                <button
                  style={styles.sortButton}
                  onClick={() => handleSort('riskScore')}
                >
                  DDI
                  <SortIndicator active={sortKey === 'riskScore'} direction={sortDirection} />
                </button>
              </th>
              <th style={styles.th}>Risk Level</th>
              <th style={styles.th}>
                <button
                  style={styles.sortButton}
                  onClick={() => handleSort('engagementTrend')}
                >
                  Trend
                  <SortIndicator active={sortKey === 'engagementTrend'} direction={sortDirection} />
                </button>
              </th>
              <th style={styles.th}>Flags</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.map((employee, index) => (
              <motion.tr
                key={employee.id}
                style={styles.tr}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
                onClick={() => navigate(`/employee/${employee.id}`)}
              >
                <td style={styles.td}>
                  <div style={styles.employeeCell}>
                    <div style={styles.employeeAvatar}>
                      {employee.name.charAt(0)}
                    </div>
                    <div>
                      <div style={styles.employeeName}>{employee.name}</div>
                      <div style={styles.employeeRole}>{employee.role}</div>
                    </div>
                  </div>
                </td>
                <td style={styles.td}>
                  <span style={styles.department}>{employee.department}</span>
                </td>
                <td style={styles.td}>
                  <div style={styles.riskScoreCell}>
                    <div style={styles.riskScoreBar}>
                      <div
                        style={{
                          ...styles.riskScoreFill,
                          width: `${employee.riskScore}%`,
                          background:
                            employee.riskLevel === 'High'
                              ? '#EF4444'
                              : employee.riskLevel === 'Medium'
                              ? '#F59E0B'
                              : '#10B981',
                        }}
                      />
                    </div>
                    <span style={styles.riskScoreValue}>{employee.riskScore}</span>
                  </div>
                </td>
                <td style={styles.td}>
                  <span
                    style={{
                      ...styles.riskBadge,
                      ...getRiskBadgeStyle(employee.riskLevel),
                    }}
                  >
                    {employee.riskLevel}
                  </span>
                </td>
                <td style={styles.td}>
                  <div style={styles.trendCell}>
                    {getTrendIcon(employee.engagementTrend)}
                    <span
                      style={{
                        ...styles.trendText,
                        color:
                          employee.engagementTrend === 'declining'
                            ? '#EF4444'
                            : employee.engagementTrend === 'improving'
                            ? '#10B981'
                            : '#9CA3AF',
                      }}
                    >
                      {employee.engagementTrend}
                    </span>
                  </div>
                </td>
                <td style={styles.td}>
                  {employee.behavioralFlags.length > 0 && (
                    <div style={styles.flagsCell}>
                      <AlertTriangle size={14} color="#F59E0B" />
                      <span>{employee.behavioralFlags.length}</span>
                    </div>
                  )}
                </td>
                <td style={styles.td}>
                  <button style={styles.viewButton}>View</button>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredAndSorted.length === 0 && (
        <div style={styles.emptyState}>
          <User size={40} color="#D1D5DB" />
          <p>No employees match your filters</p>
        </div>
      )}
    </motion.div>
  );
};

const SortIndicator: React.FC<{ active: boolean; direction: SortDirection }> = ({
  active,
  direction,
}) => (
  <span style={styles.sortIndicator}>
    {active ? (
      direction === 'asc' ? (
        <ChevronUp size={14} />
      ) : (
        <ChevronDown size={14} />
      )
    ) : (
      <ChevronDown size={14} style={{ opacity: 0.3 }} />
    )}
  </span>
);

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px 24px',
    borderBottom: '1px solid #E5E7EB',
    flexWrap: 'wrap',
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#6B7280',
  },
  select: {
    padding: '8px 32px 8px 12px',
    fontSize: '14px',
    border: '2px solid #E5E7EB',
    borderRadius: '8px',
    background: 'white',
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 8px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '16px',
  },
  resultCount: {
    fontSize: '14px',
    color: '#9CA3AF',
    marginLeft: 'auto',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '14px 20px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #E5E7EB',
    background: '#FAFAFA',
  },
  sortButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: 0,
  },
  sortIndicator: {
    display: 'flex',
    alignItems: 'center',
  },
  tr: {
    borderBottom: '1px solid #F3F4F6',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
  },
  td: {
    padding: '16px 20px',
    fontSize: '14px',
    color: '#374151',
  },
  employeeCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  employeeAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #69277F 0%, #FF68C5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
  },
  employeeName: {
    fontWeight: 500,
    color: '#1F2937',
  },
  employeeRole: {
    fontSize: '13px',
    color: '#6B7280',
  },
  department: {
    padding: '4px 10px',
    background: '#F3F4F6',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#4B5563',
  },
  riskScoreCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  riskScoreBar: {
    width: '60px',
    height: '6px',
    background: '#E5E7EB',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  riskScoreFill: {
    height: '100%',
    borderRadius: '3px',
  },
  riskScoreValue: {
    fontWeight: 600,
    fontSize: '14px',
    minWidth: '24px',
  },
  riskBadge: {
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
  },
  trendCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  trendText: {
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'capitalize',
  },
  flagsCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#F59E0B',
    fontSize: '13px',
    fontWeight: 500,
  },
  viewButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '2px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#6B7280',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    color: '#9CA3AF',
    gap: '12px',
  },
};

export default EmployeeTable;

